/** Supplier Master List table (Demo Mode only). Searchable/filterable supplier master list -
 * swapped in for the real Suppliers list on the Procurement Suppliers screen only when
 * isDemoModeEnabled() is true (see ../../index.tsx), so the production supplier list/API is
 * left untouched. Data is mock-only, see src/mocks/data/supplierMasterListData.ts. Clicking a
 * row opens the existing Supplier Profile screen (Procurement Suppliers edit form). */

import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, FileDownloadOutlined, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import CustomPagination from '../../../../components/forms/Pagination';
import { useDemoScenario } from '../../../../contexts/DemoScenarioContext';
import { SupplierApprovalStatus } from '../../../../mocks/data/supplierGovernanceData';
import {
  ASSIGNED_SITE_OPTIONS,
  SUPPLIER_CATEGORIES,
  SupplierCertificationStatus,
  SupplierCoverageType,
  SupplierMasterListEntry,
  SupplierOperationalStatus,
  supplierMasterList,
} from '../../../../mocks/data/supplierMasterListData';
import {
  SupplierApprovalStatusChip,
  SupplierCertificationStatusChip,
  SupplierExceptionsChip,
  SupplierOperationalStatusChip,
} from './SupplierMasterListChips';

const ALL = 'All';
const EXCEPTIONS_FILTER_OPTIONS = ['All', 'None', 'Has exceptions'] as const;
const EXCEPTIONS_FILTER_LABEL_KEY: Record<typeof EXCEPTIONS_FILTER_OPTIONS[number], string> = {
  All: 'masterList.all',
  None: 'exceptionsFilterNone',
  'Has exceptions': 'exceptionsFilterHasExceptions',
};

interface Filters {
  approvalStatus: SupplierApprovalStatus | typeof ALL;
  operationalStatus: SupplierOperationalStatus | typeof ALL;
  category: string;
  coverage: SupplierCoverageType | typeof ALL;
  certificationStatus: SupplierCertificationStatus | typeof ALL;
  openExceptions: typeof EXCEPTIONS_FILTER_OPTIONS[number];
  assignedSite: string;
}

const DEFAULT_FILTERS: Filters = {
  approvalStatus: ALL,
  operationalStatus: ALL,
  category: ALL,
  coverage: ALL,
  certificationStatus: ALL,
  openExceptions: 'All',
  assignedSite: ALL,
};

const SupplierMasterList = () => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const { addReferral } = useDemoScenario();

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [requestName, setRequestName] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const filteredSuppliers = useMemo(() => {
    return supplierMasterList.filter((supplier) => {
      if (searchTerm && !supplier.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (filters.approvalStatus !== ALL && supplier.approvalStatus !== filters.approvalStatus) return false;
      if (filters.operationalStatus !== ALL && supplier.operationalStatus !== filters.operationalStatus) return false;
      if (filters.category !== ALL && supplier.category !== filters.category) return false;
      if (filters.coverage !== ALL && supplier.coverage !== filters.coverage) return false;
      if (filters.certificationStatus !== ALL && supplier.certificationStatus !== filters.certificationStatus) return false;
      if (filters.openExceptions === 'None' && supplier.openExceptions !== 0) return false;
      if (filters.openExceptions === 'Has exceptions' && supplier.openExceptions === 0) return false;
      if (filters.assignedSite !== ALL && !supplier.assignedSites.includes(filters.assignedSite)) return false;
      return true;
    });
  }, [searchTerm, filters]);

  const maxPages = Math.max(1, Math.ceil(filteredSuppliers.length / pageSize));

  const pagedSuppliers = useMemo(() => {
    return filteredSuppliers.slice(page * pageSize, (page + 1) * pageSize);
  }, [filteredSuppliers, page]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filters]);

  const handleClear = () => {
    setSearchTerm('');
    setFilters(DEFAULT_FILTERS);
  };

  const handleRowClick = (supplier: SupplierMasterListEntry) => {
    navigate(`/procurement/suppliers/edit/${supplier.supplierId}`);
  };

  const handleAddSupplier = () => {
    navigate('/procurement/suppliers/create');
  };

  const handleSubmitRequest = () => {
    if (!requestName.trim()) return;
    addReferral({
      supplierName: requestName.trim(),
      requestedByProduct: t('masterList.manualRequest', 'Manual request'),
      reason: requestReason.trim() || t('masterList.noReasonGiven', 'No reason given'),
    });
    setIsRequestDialogOpen(false);
    setRequestName('');
    setRequestReason('');
    setConfirmationMessage(t('masterList.requestSubmitted', 'Supplier request submitted for review'));
  };

  // lazy: client-side CSV export of the currently filtered rows only - no backend export
  // endpoint exists yet for the demo master list. Upgrade path: server-side export job if this
  // needs to cover more than the visible page.
  const handleExport = () => {
    const headers = [
      t('supplierName', 'Supplier name'),
      t('masterList.category', 'Category'),
      t('masterList.approvalStatus', 'Approval status'),
      t('masterList.operationalStatus', 'Operational status'),
      t('masterList.coverage', 'Coverage'),
      t('masterList.assignedSites', 'Assigned sites'),
      t('masterList.activeCatalogues', 'Active catalogues'),
      t('masterList.certificationStatus', 'Certification status'),
      t('masterList.openExceptions', 'Open exceptions'),
      t('masterList.lastReviewed', 'Last reviewed'),
    ];
    const rows = filteredSuppliers.map((supplier) => [
      supplier.name,
      supplier.category,
      supplier.approvalStatus,
      supplier.operationalStatus,
      supplier.coverage,
      supplier.assignedSites.join('; '),
      supplier.activeCatalogues,
      supplier.certificationStatus,
      supplier.openExceptions,
      supplier.lastReviewed,
    ]);
    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'supplier-master-list.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={4}>
      <Stack direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={2}>
        <Typography variant="textMD"
          fontWeight={600}>
          {t('masterList.title', 'Supplier master list')}
        </Typography>
        <Stack direction="row"
          spacing={2}
          flexWrap="wrap">
          <Button variant="secondary"
            size="lg"
            onClick={handleExport}
            startIcon={<SvgIcon><FileDownloadOutlined /></SvgIcon>}>
            {t('masterList.export', 'Export')}
          </Button>
          <Button variant="secondary"
            size="lg"
            onClick={() => setIsRequestDialogOpen(true)}>
            {t('masterList.requestSupplier', 'Request supplier')}
          </Button>
          <Button variant="secondary"
            size="lg"
            onClick={() => navigate('/procurement/suppliers/onboarding')}>
            {t('onboardingWizardTitle', 'Supplier Onboarding')}
          </Button>
          <Button variant="primary"
            size="lg"
            onClick={handleAddSupplier}
            startIcon={<SvgIcon><Add /></SvgIcon>}>
            {t('masterList.addSupplier', 'Add supplier')}
          </Button>
        </Stack>
      </Stack>

      {/* Quick filters + search row */}
      <Grid container
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}>
        <Grid item
          sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            size="small"
            label={t('search')}
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240, mb: 0 }}
          />
        </Grid>

        <Grid item>
          <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
        </Grid>

        <Grid item>
          <Grid container
            spacing={1}
            alignItems="center">
            {(['Active', 'OnHold', 'Inactive', 'Onboarding'] as SupplierOperationalStatus[]).map((status) => (
              <Grid item
                key={status}>
                <Chip
                  label={t(`status${status}`, status)}
                  color={filters.operationalStatus === status ? 'primary' : 'default'}
                  onClick={() => setFilters((prev) => ({ ...prev, operationalStatus: prev.operationalStatus === status ? ALL : status }))}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Advanced filters accordion */}
      <Accordion
        variant="borderless"
        expanded={isAdvancedOpen}
        onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
      >
        <AccordionSummary>
          <Grid container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                <Grid item>
                  {t('filters.advanced', 'Advanced filters')}
                </Grid>
                <Grid item
                  style={{ marginTop: '8px' }}>
                  {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                size="md"
                variant="secondary"
                onClick={handleClear}>
                {t('filters.clear', 'Clear filters')}
              </Button>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container
            spacing={3}>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('masterList.approvalStatus', 'Approval status')}</InputLabel>
                <Select
                  value={filters.approvalStatus}
                  displayEmpty
                  notched
                  label={t('masterList.approvalStatus', 'Approval status')}
                  onChange={(event) => setFilters({ ...filters, approvalStatus: event.target.value as Filters['approvalStatus'] })}
                >
                  <MenuItem value={ALL}>{t('masterList.all', 'All')}</MenuItem>
                  {Object.values(SupplierApprovalStatus).map((status) => (
                    <MenuItem key={status}
                      value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('masterList.operationalStatus', 'Operational status')}</InputLabel>
                <Select
                  value={filters.operationalStatus}
                  displayEmpty
                  notched
                  label={t('masterList.operationalStatus', 'Operational status')}
                  onChange={(event) => setFilters({ ...filters, operationalStatus: event.target.value as Filters['operationalStatus'] })}
                >
                  <MenuItem value={ALL}>{t('masterList.all', 'All')}</MenuItem>
                  {(['Active', 'OnHold', 'Inactive', 'Onboarding'] as SupplierOperationalStatus[]).map((status) => (
                    <MenuItem key={status}
                      value={status}>{t(`status${status}`, status)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('masterList.category', 'Category')}</InputLabel>
                <Select
                  value={filters.category}
                  displayEmpty
                  notched
                  label={t('masterList.category', 'Category')}
                  onChange={(event) => setFilters({ ...filters, category: event.target.value })}
                >
                  <MenuItem value={ALL}>{t('masterList.all', 'All')}</MenuItem>
                  {SUPPLIER_CATEGORIES.map((category) => (
                    <MenuItem key={category}
                      value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('masterList.coverage', 'Coverage')}</InputLabel>
                <Select
                  value={filters.coverage}
                  displayEmpty
                  notched
                  label={t('masterList.coverage', 'Coverage')}
                  onChange={(event) => setFilters({ ...filters, coverage: event.target.value as Filters['coverage'] })}
                >
                  <MenuItem value={ALL}>{t('masterList.all', 'All')}</MenuItem>
                  {(['National', 'Regional', 'SingleSite'] as SupplierCoverageType[]).map((coverage) => (
                    <MenuItem key={coverage}
                      value={coverage}>{coverage}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('masterList.certificationStatus', 'Certification status')}</InputLabel>
                <Select
                  value={filters.certificationStatus}
                  displayEmpty
                  notched
                  label={t('masterList.certificationStatus', 'Certification status')}
                  onChange={(event) => setFilters({ ...filters, certificationStatus: event.target.value as Filters['certificationStatus'] })}
                >
                  <MenuItem value={ALL}>{t('masterList.all', 'All')}</MenuItem>
                  {(['Valid', 'ExpiringSoon', 'Expired', 'None'] as SupplierCertificationStatus[]).map((status) => (
                    <MenuItem key={status}
                      value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('masterList.openExceptions', 'Open exceptions')}</InputLabel>
                <Select
                  value={filters.openExceptions}
                  displayEmpty
                  notched
                  label={t('masterList.openExceptions', 'Open exceptions')}
                  onChange={(event) => setFilters({ ...filters, openExceptions: event.target.value as Filters['openExceptions'] })}
                >
                  {EXCEPTIONS_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option}
                      value={option}>{t(EXCEPTIONS_FILTER_LABEL_KEY[option], option)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('masterList.assignedSite', 'Assigned site')}</InputLabel>
                <Select
                  value={filters.assignedSite}
                  displayEmpty
                  notched
                  label={t('masterList.assignedSite', 'Assigned site')}
                  onChange={(event) => setFilters({ ...filters, assignedSite: event.target.value })}
                >
                  <MenuItem value={ALL}>{t('masterList.all', 'All')}</MenuItem>
                  {ASSIGNED_SITE_OPTIONS.map((site) => (
                    <MenuItem key={site}
                      value={site}>{site}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Section>
        {filteredSuppliers.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('masterList.title', 'Supplier master list')}
            searchTerm={searchTerm}
            onClear={handleClear}
            onAdd={handleAddSupplier}
            onAddIsCreating={false}
            addItemText={t('masterList.addSupplier', 'Add supplier')}
            onAddIsDisabled={false}
            withPaginationShown={false}
          />
        ) : (
          <>
            <TableHeader>
              {t('masterList.title', 'Supplier master list')}
              <Pill text={t('supplierCount', '{{count}} suppliers', { count: filteredSuppliers.length })} />
            </TableHeader>
            <TableContainer>
              <Table sx={{ minWidth: '1400px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('supplierName', 'Supplier name')}</TableCell>
                    <TableCell>{t('masterList.category', 'Category')}</TableCell>
                    <TableCell>{t('masterList.approvalStatus', 'Approval status')}</TableCell>
                    <TableCell>{t('masterList.operationalStatus', 'Operational status')}</TableCell>
                    <TableCell>{t('masterList.coverage', 'Coverage')}</TableCell>
                    <TableCell>{t('masterList.assignedSites', 'Assigned sites')}</TableCell>
                    <TableCell>{t('masterList.activeCatalogues', 'Active catalogues')}</TableCell>
                    <TableCell>{t('masterList.certificationStatus', 'Certification status')}</TableCell>
                    <TableCell>{t('masterList.openExceptions', 'Open exceptions')}</TableCell>
                    <TableCell>{t('masterList.lastReviewed', 'Last reviewed')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedSuppliers.map((supplier) => (
                    <TableRow
                      key={supplier.supplierId}
                      hover
                      onClick={() => handleRowClick(supplier)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{supplier.name}</TableCell>
                      <TableCell>{supplier.category}</TableCell>
                      <TableCell><SupplierApprovalStatusChip status={supplier.approvalStatus} /></TableCell>
                      <TableCell><SupplierOperationalStatusChip status={supplier.operationalStatus} /></TableCell>
                      <TableCell>{supplier.coverage}</TableCell>
                      <TableCell>{supplier.assignedSites.join(', ') || '-'}</TableCell>
                      <TableCell>{supplier.activeCatalogues}</TableCell>
                      <TableCell><SupplierCertificationStatusChip status={supplier.certificationStatus} /></TableCell>
                      <TableCell><SupplierExceptionsChip count={supplier.openExceptions} /></TableCell>
                      <TableCell>{supplier.lastReviewed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      <Dialog open={isRequestDialogOpen}
        onClose={() => setIsRequestDialogOpen(false)}
        fullWidth
        maxWidth="sm">
        <DialogTitle>{t('masterList.requestSupplier', 'Request supplier')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}
            sx={{ mt: 1 }}>
            <TextField
              label={t('supplierName', 'Supplier name')}
              value={requestName}
              onChange={(event) => setRequestName(event.target.value)}
              fullWidth
            />
            <TextField
              label={t('masterList.reason', 'Reason')}
              value={requestReason}
              onChange={(event) => setRequestReason(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setIsRequestDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary"
            onClick={handleSubmitRequest}
            disabled={!requestName.trim()}>
            {t('masterList.submitRequest', 'Submit request')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!confirmationMessage}
        autoHideDuration={4000}
        onClose={() => setConfirmationMessage('')}
        message={confirmationMessage}
      />
    </Stack>
  );
};

export default SupplierMasterList;
